import { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { countries, muslimMajorityNames } from "@/data/countries";

const geoUrl = "/world-countries.json";
const profiledNames = Object.fromEntries(
  countries.map((c) => [
    {
      malaysia: "Malaysia", uae: "United Arab Emirates", egypt: "Egypt", morocco: "Morocco",
      indonesia: "Indonesia", saudi: "Saudi Arabia", turkiye: "Turkey", qatar: "Qatar",
      tanzania: "Tanzania",
    }[c.slug],
    c,
  ])
);

export const WorldMap = ({ onSelect, selected, interactive = true }) => {
  const navigate = useNavigate();
  const lastTap = useRef({ slug: null, at: 0 });
  const majority = useMemo(() => new Set(muslimMajorityNames), []);

  return (
    <div data-testid="world-map" className="w-full">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{ center: [42, 18], scale: 215 }}
        width={960}
        height={540}
        style={{ width: "100%", height: "auto" }}
      >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const name = geo.properties.name;
                const profiled = profiledNames[name];
                const isMajority = majority.has(name);
                const isSelected = selected && profiled && selected.slug === profiled.slug;
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    data-testid={profiled ? `map-country-${profiled.slug}` : undefined}
                    onClick={() => {
                      if (interactive && profiled && onSelect) onSelect(profiled);
                    }}
                    style={{
                      default: {
                        fill: isSelected ? "#D9777F" : profiled ? "#3E9B5F" : isMajority ? "#CDE8D4" : "#F6F1E2",
                        stroke: "#FAF7F2",
                        strokeWidth: 0.5,
                        outline: "none",
                        cursor: interactive && profiled ? "pointer" : "default",
                        transition: "fill 0.3s",
                      },
                      hover: {
                        fill: profiled ? "#D9777F" : isMajority ? "#A8D4B5" : "#F6F1E2",
                        stroke: "#FAF7F2",
                        strokeWidth: 0.5,
                        outline: "none",
                        cursor: interactive && profiled ? "pointer" : "default",
                      },
                      pressed: { fill: "#B95A63", outline: "none" },
                    }}
                  />
                );
              })
            }
          </Geographies>
          {countries.map((c) => (
            <Marker
              key={c.slug}
              coordinates={c.coords}
              onClick={() => {
                if (!interactive) return;
                const now = Date.now();
                if (lastTap.current.slug === c.slug && now - lastTap.current.at < 350) {
                  lastTap.current = { slug: null, at: 0 };
                  navigate(`/countries/${c.slug}`);
                  return;
                }
                lastTap.current = { slug: c.slug, at: now };
                if (onSelect) onSelect(c);
              }}
              style={{ default: { cursor: interactive ? "pointer" : "default" } }}
            >
              <g className="group" data-testid={`map-pin-${c.slug}`}>
                <circle r="10" fill="#D9777F" opacity="0.25">
                  <animate attributeName="r" values="6;14;6" dur="2.4s" repeatCount="indefinite" />
                  <animate attributeName="opacity" values="0.35;0;0.35" dur="2.4s" repeatCount="indefinite" />
                </circle>
                <circle r="4" fill="#D9777F" stroke="#FAF7F2" strokeWidth="1.5" />
                <path
                  d="M0 -21 C -4.6 -21 -7.6 -17.6 -7.6 -13.8 C -7.6 -8.8 0 -4.6 0 -4.6 C 0 -4.6 7.6 -8.8 7.6 -13.8 C 7.6 -17.6 4.6 -21 0 -21 Z"
                  fill="#B95A63"
                  stroke="#FAF7F2"
                  strokeWidth="1.2"
                />
                <circle cx="0" cy="-13.8" r="2.3" fill="#FAF7F2" />
                <g
                  className="transition-opacity duration-200 opacity-0 group-hover:opacity-100"
                  pointerEvents="none"
                >
                  <text
                    y="-29"
                    textAnchor="middle"
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: "9px",
                      fontWeight: 500,
                      fill: "#6B5410",
                      paintOrder: "stroke",
                      stroke: "#FAF7F2",
                      strokeWidth: "2.5px",
                    }}
                  >
                    {c.name.toUpperCase()}
                  </text>
                </g>
              </g>
            </Marker>
          ))}
      </ComposableMap>
    </div>
  );
};
